/**
 * Rezepte zur Bestellung.
 *
 * Hört auf `tracking:order` aus der Sendungsverfolgungs-Section und sortiert
 * die Rezeptkarten so, dass die zu den bestellten Produkten passenden vorne
 * stehen. Gematcht wird über den Produkttitel: die Bestellung liefert Titel,
 * die Artikel tragen ihre verwendeten Produkte im Metafeld `used_products`.
 *
 * Titel statt Handle: das Feld `lineItems.product` der Admin API verlangt den
 * Scope `read_products`, und ohne ihn schlägt die ganze Abfrage fehl. Wird der
 * Scope irgendwann ergänzt, ist Handle die stabilere Grundlage — Titel ändern
 * sich, Handles nicht.
 */

class TrackingRecipes extends HTMLElement {
  connectedCallback() {
    this.grid = this.querySelector('[data-recipes-grid]');
    this.headingEl = this.querySelector('[data-recipes-heading]');
    this.cards = [...this.querySelectorAll('[data-recipe]')];
    this.visible = Number.parseInt(this.dataset.visible || '3', 10);
    this.visibleMobile = Number.parseInt(
      this.dataset.visibleMobile || this.dataset.visible || '3',
      10
    );

    // Mobil ist das Raster zweispaltig, am Desktop dreispaltig - deshalb passt
    // dort nicht dieselbe Anzahl, ohne dass eine halbe Reihe stehen bleibt.
    this.wide = window.matchMedia('(min-width: 750px)');
    this.wide.addEventListener('change', () => this.showFirst());

    this.showFirst(this.cards);

    document.addEventListener('tracking:order', (event) => {
      this.applyOrder(event.detail?.products || []);
    });
  }

  /**
   * Zeigt die ersten Karten der aktuellen Reihenfolge. Ohne Argument gilt die
   * zuletzt gesetzte - so bleibt die Sortierung beim Drehen des Geraets stehen.
   */
  showFirst(cards) {
    if (cards) this.order = cards;

    const limit = this.wide.matches ? this.visible : this.visibleMobile;

    (this.order || this.cards).forEach((card, index) => {
      card.hidden = index >= limit;
    });
  }

  /*
   * Zwei Stufen. Erst exakt auf den Produkttitel - das ist der saubere Treffer
   * aus dem Metafeld. Findet sich nichts, wird locker ueber Stichworte aus den
   * bestellten Produkten gesucht, gegen die Rezeptprodukte UND den Rezepttitel.
   * Grund: das Metafeld ist nur an einem Teil der Artikel gepflegt, und ohne
   * die zweite Stufe steht bei einer Bowl-Bestellung dreimal Matcha Latte da.
   */
  applyOrder(products) {
    const wanted = products.map(normalize).filter(Boolean);
    if (!wanted.length) return;

    let matches = this.score((card) =>
      (card.dataset.recipeProducts || '')
        .split('|')
        .map(normalize)
        .filter((title) => title && wanted.includes(title)).length
    );

    if (!matches.length) {
      const terms = [...new Set(products.flatMap(keywords))];
      if (terms.length) {
        matches = this.score((card) => {
          const haystack = normalize(
            `${card.dataset.recipeProducts || ''} ${card.querySelector('[data-recipe-title]')?.textContent || ''}`
          );
          return terms.filter((term) => haystack.includes(term)).length;
        });
      }
    }

    if (!matches.length) return;

    const ordered = [...matches.map((entry) => entry.card)];
    this.cards.forEach((card) => {
      if (!ordered.includes(card)) ordered.push(card);
    });

    ordered.forEach((card) => this.grid.appendChild(card));
    this.showFirst(ordered);

    const heading = this.dataset.headingMatch;
    if (heading && this.headingEl) this.headingEl.textContent = heading;
  }

  /** Karten bewerten, Treffer zuerst, Reihenfolge sonst unveraendert. */
  score(count) {
    return this.cards
      .map((card) => ({ card, hits: count(card) }))
      .filter((entry) => entry.hits > 0)
      .sort((a, b) => b.hits - a.hits);
  }
}

/*
 * Woerter, die in fast jedem Produktnamen stehen und deshalb nichts ueber den
 * Inhalt aussagen. Ohne die Liste matcht "Probierpaket" auf alles.
 */
const STOPWORDS = new Set([
  'bio', 'brekky', 'gratis', 'kopie', 'limitiertes', 'limitierte', 'paket',
  'probierpaket', 'produkt', 'refill', 'sorten', 'sommer', 'starter', 'vegan',
  'veganes', 'glaesern', 'glasern', 'set',
]);

/** Aussagekraeftige Woerter aus einem Produktnamen. */
function keywords(title) {
  return normalize(title)
    .split(' ')
    .filter((word) => word.length >= 4 && !STOPWORDS.has(word));
}

/** Grosskleinschreibung, Leerzeichen und Bindestriche sollen egal sein. */
function normalize(value) {
  return String(value || '')
    .toLowerCase()
    .replace(/[\s\-–—]+/g, ' ')
    .trim();
}

if (!customElements.get('tracking-recipes')) {
  customElements.define('tracking-recipes', TrackingRecipes);
}
